import { type AaguidCatalog, NULL_AAGUID } from './aaguid-catalog';
import {
  AuthenticatorFormFactor,
  FORM_FACTOR_ICONS,
} from './authenticator-form-factor';
import { PLATFORM_AAGUIDS } from './platform-authenticators';

const AAGUID_PREFIX_LENGTH = 8;

interface ResolveAuthenticatorParams {
  aaguid: string | null;
  backupEligible: boolean;
  catalog: AaguidCatalog | undefined;
}

interface ResolveFormFactorParams {
  normalizedAaguid: string | null;
  backupEligible: boolean;
  modelName: string | null;
}

function normalizeAaguid(aaguid: string | null) {
  const normalized = aaguid?.trim().toLowerCase() || null;

  return normalized === NULL_AAGUID ? null : normalized;
}

export function getPasskeyAuthenticatorName(
  aaguid: string | null,
  catalog: AaguidCatalog | undefined
) {
  const normalizedAaguid = normalizeAaguid(aaguid);

  if (!normalizedAaguid) {
    return null;
  }

  return catalog?.[normalizedAaguid] ?? null;
}

// Unnamed models show their AAGUID prefix, so a stale or unloaded catalog is
// visible rather than looking like a model with no name.
export function getPasskeyModelLabel(
  aaguid: string | null,
  catalog: AaguidCatalog | undefined
) {
  const normalizedAaguid = normalizeAaguid(aaguid);

  if (!normalizedAaguid) {
    return null;
  }

  return (
    catalog?.[normalizedAaguid] ??
    `${normalizedAaguid.slice(0, AAGUID_PREFIX_LENGTH)}…`
  );
}

function resolveFormFactor({
  normalizedAaguid,
  backupEligible,
  modelName,
}: ResolveFormFactorParams) {
  if (backupEligible) {
    return AuthenticatorFormFactor.PASSWORD_MANAGER;
  }
  if (normalizedAaguid && PLATFORM_AAGUIDS.has(normalizedAaguid)) {
    return AuthenticatorFormFactor.PLATFORM;
  }
  if (modelName) {
    return AuthenticatorFormFactor.SECURITY_KEY;
  }
  return AuthenticatorFormFactor.UNKNOWN;
}

export function resolveAuthenticator({
  aaguid,
  backupEligible,
  catalog,
}: ResolveAuthenticatorParams) {
  const normalizedAaguid = normalizeAaguid(aaguid);
  const modelName = getPasskeyAuthenticatorName(normalizedAaguid, catalog);
  const formFactor = resolveFormFactor({
    normalizedAaguid,
    backupEligible,
    modelName,
  });

  return { modelName, formFactor, icon: FORM_FACTOR_ICONS[formFactor] };
}
